import { t } from 'i18next'
import React, { FunctionComponent, useState, useEffect, useRef } from 'react'

import { ClinTheme } from '../../ClinTheme'
import { ClinIcon } from '../ClinIcon'
import { ClinIconPathName } from '../ClinIcon/ClinIcon.paths'
import { ClinText } from '../ClinText'
import {
  ActionButton,
  CloseIconButton,
  DismissButton,
  MessageText,
  NotificationContainer,
  VerticalLine
} from './ClinNotification.styles'

export interface IClinNotificationAction {
  label: string
  onClick: () => void
}

interface ClinNotificationProps {
  title: string
  /**
   * Optional next step (e.g. Begin order). When given, the notification uses its second
   * look: dark green, a message that may wrap to two lines, the action and a close icon.
   * Position and auto-hide stay the same. Without it the notification is unchanged.
   */
  action?: IClinNotificationAction
  onClose: (e?: React.MouseEvent<HTMLButtonElement>) => void
  autoHideTimer?: number
  style?: React.CSSProperties
  className?: string
}

export const ClinNotification: FunctionComponent<ClinNotificationProps> = ({
  title,
  action,
  onClose,
  autoHideTimer = 10000,
  style,
  className
}) => {
  const [isVisible, setIsVisible] = useState(true)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const handleClose = () => {
    setIsVisible(false)
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    onClose && onClose()
  }

  useEffect(() => {
    if (autoHideTimer > 0) {
      const timer = setTimeout(() => {
        handleClose()
      }, autoHideTimer)

      timerRef.current = timer

      return () => {
        clearTimeout(timer)
        timerRef.current = null
      }
    }
  }, [autoHideTimer])

  if (isVisible && action) {
    return (
      <NotificationContainer
        $withAction
        style={style}
        className={className}
        role="status"
        data-testid="notification-container"
      >
        <ClinIcon
          iconSize={ClinTheme.fontSizes[4]}
          iconName={ClinIconPathName.Success}
          iconFill={ClinTheme.colors.white}
          aria-hidden
          data-testid="clin-icon"
        />
        <MessageText>
          <ClinText
            fontWeight={ClinTheme.fontWeights.medium}
            fontSize={ClinTheme.fontSizes[1]}
            color={ClinTheme.colors.white}
            data-testid="clin-text"
          >
            {title}
          </ClinText>
        </MessageText>
        <VerticalLine $withAction aria-hidden data-testid="vertical-line" />
        <ActionButton
          type="button"
          onClick={() => {
            handleClose()
            action.onClick()
          }}
          data-testid="notification-action"
        >
          {action.label}
        </ActionButton>
        <CloseIconButton
          type="button"
          onClick={handleClose}
          aria-label={t('common:buttons.dismiss')}
          data-testid="dismiss-button"
        >
          <ClinIcon
            iconName={ClinIconPathName.Cross}
            iconFill={ClinTheme.colors.white}
            iconSize={14}
          />
        </CloseIconButton>
      </NotificationContainer>
    )
  }

  return (
    <>
      {isVisible && (
        <NotificationContainer
          style={style}
          className={className}
          data-testid="notification-container"
        >
          <ClinIcon
            style={{
              marginLeft: ClinTheme.space[3],
              marginTop: ClinTheme.space[2]
            }}
            iconSize={ClinTheme.fontSizes[4]}
            iconName={ClinIconPathName.Success}
            iconFill={ClinTheme.colors.aquaGreen}
            data-testid="clin-icon"
          />
          <ClinText
            fontWeight={ClinTheme.fontWeights.medium}
            fontSize={ClinTheme.fontSizes[1]}
            color={ClinTheme.colors.white}
            whiteSpace="nowrap"
            data-testid="clin-text"
          >
            {title}
          </ClinText>
          <VerticalLine data-testid="vertical-line"></VerticalLine>
          <DismissButton onClick={handleClose} data-testid="dismiss-button">
            {t('common:buttons.dismiss')}
          </DismissButton>
        </NotificationContainer>
      )}
    </>
  )
}

export default ClinNotification
